import type {
  GraphEdgeSettingsStyle,
  GroupEdgeSettings,
  GroupEdgeSettingsId,
  PathLookupEdgeSettings,
  ProtocolEdgeType,
  TopologyEdgeSettings,
} from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { type FC, memo, useRef } from 'react';
import type { ReactNode } from 'react';

import { Tooltip } from '@ipfabric/ui-components/Tooltip/Tooltip';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import classNames from 'classnames';
import { Button } from 'components/Button/Button';
import { useGraphContextSelectors } from 'components/Graph/GraphContext';
import {
  GraphProtocolsSettings,
  type SaveSettingsCommand,
} from 'components/Graph/GraphSidePanel/GraphProtocolsSettingsWindow/GraphProtocolsSettings';
import { getLabels } from 'components/Graph/GraphSidePanel/helpers/getLabels';
import { deleteEdgeGroup } from 'components/Graph/commands/deleteEdgeGroup';
import { setGroupedEdge } from 'components/Graph/commands/setGroupedEdge';
import { setProtocolSettings } from 'components/Graph/commands/setProtocolSettings';
import { setVisibleEdge } from 'components/Graph/commands/setVisibleEdge';
import { DnDIcon } from 'components/icons/DnDIcon';
import { useLocale } from 'containers/App/LocaleContext';
import { type Location, useNavigate } from 'react-router-dom';
import { GraphStpInstances } from './GraphStpInstances';
import { ProtocolGroupActions } from './ProtocolGroupActions';
import { useProtocolDragAndDrop } from './useProtocolDragAndDrop';
import type { StpInstancesHook } from './useStpInstances';

export interface ProtocolProps {
  borderBottom: boolean;
  closePane?: () => void;
  disableColor: boolean;
  disableControls: boolean;
  disableDeleteButton: boolean;
  disableDrag: boolean;
  disableHover: boolean;
  edgeSettingsId: GroupEdgeSettingsId | null;
  childProtocols?: (TopologyEdgeSettings | PathLookupEdgeSettings)[];
  groupGrouped: boolean;
  groupId: GroupEdgeSettings['id'] | null;
  groupIndex: number;
  groupVisible: boolean;
  grouped: GroupEdgeSettings['grouped'] | TopologyEdgeSettings['grouped'];
  inGroup: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  openPane?: (paneContent: ReactNode) => void;
  protocolIndex: number;
  protocolName?: ProtocolEdgeType;
  style?: GraphEdgeSettingsStyle;
  title: string;
  visible: boolean | null;
  stpInstancesHook?: StpInstancesHook;
}

const BareTopologyLayoutSettingsProtocolsProtocol: FC<ProtocolProps> = (
  props,
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TBD cleanup eventually
) => {
  const msg = useLocale();
  const rowRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const ctxProps = useGraphContextSelectors({
    presetDraftState: prop('layoutSettings', 'presetDraftState'),
    dispatch: prop('dispatch'),
    draggedProtocol: prop('layoutSettings', 'draggedProtocol'),
    presetEditMode: prop('layoutSettings', 'presetEditMode'),
    preset: prop('layoutSettings', 'presetDraft'),
    stpiIsolated: (s) =>
      s.technologies.selectedTechnologies.stpInstances?.isolate ?? false,
  });

  const rowIsGroupHeading: boolean =
    props.groupId !== null && props.protocolName === undefined;
  const protocolIsDragged =
    !!props.protocolName && ctxProps.draggedProtocol === props.protocolName;

  useProtocolDragAndDrop({
    disableDrag: props.disableDrag,
    dispatch: ctxProps.dispatch,
    presetEditMode: ctxProps.presetEditMode,
    groupIndex: props.groupIndex,
    protocolIndex: props.protocolIndex,
    protocolName: props.protocolName,
    rowRef,
    style: props.style,
    title: props.title,
    rowIsGroupHeading,
  });

  const handleEdgeVisibilityIconClick = () => {
    if (props.edgeSettingsId) {
      ctxProps.dispatch(setVisibleEdge, {
        edgeSettingsId: props.edgeSettingsId,
        visible: !props.visible,
      });
    }
  };

  const handleGroupEdgeIconClick = useStableCallback(() => {
    if (props.edgeSettingsId) {
      ctxProps.dispatch(setGroupedEdge, {
        grouped: !props.grouped,
        edgeSettingsId: props.edgeSettingsId,
      });
    }
  });

  const handleSaveEdgeSettings = useStableCallback(
    (command: SaveSettingsCommand, historyAttributes?: Location) => {
      ctxProps.dispatch(setProtocolSettings, {
        groupId: props.groupId,
        protocolName: props.protocolName,
        values: command.values,
      });
      // Redirect user to next page after saving from can leave dialogue
      if (historyAttributes) {
        navigate(historyAttributes.pathname, {
          ...historyAttributes,
          state: { canLeave: true },
        });
      }
    },
  );

  const handleEdgeGroupDelete = useStableCallback(() => {
    if (props.groupId !== null) {
      ctxProps.dispatch(deleteEdgeGroup, {
        groupId: props.groupId,
      });
    }
  });

  const handleOpenProtocolSettingsWindow = useStableCallback(() => {
    if (props.openPane && props.closePane) {
      props.openPane(
        <GraphProtocolsSettings
          presetDraftState={ctxProps.presetDraftState}
          groupId={props.groupId}
          labels={getLabels(ctxProps.preset, props.groupId, props.protocolName)}
          onClose={props.closePane}
          onSave={handleSaveEdgeSettings}
          preset={ctxProps.preset}
          protocolName={props.protocolName}
          protocolTitle={props.title}
        />,
      );
    }
  });

  return (
    <>
      <div
        className={classNames(
          'graph-layout-settings-protocols__protocol',
          !props.disableHover &&
            !ctxProps.draggedProtocol &&
            'graph-layout-settings-protocols__protocol--hover',
          props.borderBottom &&
            'graph-layout-settings-protocols__protocol--border-bottom',
          protocolIsDragged && 'graph-draggable-item__box-border mx-5',
          'flex flex-1 flex-row',
          ctxProps.presetEditMode && !props.disableDrag && 'cursor-move',
          'py-lgu-5 px-5',
        )}
        onMouseEnter={() => {
          props.onMouseEnter?.();
        }}
        onMouseLeave={() => {
          props.onMouseLeave?.();
        }}
        ref={rowRef}
      >
        <div
          className={classNames(
            'flex flex-1 flex-row items-center justify-center',
            protocolIsDragged && 'invisible',
          )}
        >
          {ctxProps.presetEditMode && !props.disableDrag && (
            <DnDIcon
              title='Drag to move'
              width={6}
              height={10}
              className='mr-2.5'
            />
          )}
          {!rowIsGroupHeading && <span className='pl-lgu-10' />}
          {!props.disableColor && (
            <span
              className='graph-layout-settings-protocols__color mr-2.5'
              style={{ background: props.style?.color }}
            />
          )}
          <span className={classNames('flex-1', props.inGroup && 'font-bold')}>
            {props.title}
          </span>
          {!props.disableControls && (
            <>
              {props.childProtocols && (
                <ProtocolGroupActions
                  childProtocols={props.childProtocols}
                  // individual protocols in invisible or grouped group cannot be manipulated
                  disabled={
                    ctxProps.stpiIsolated || props.grouped || !props.visible
                  }
                />
              )}
              {ctxProps.stpiIsolated && !ctxProps.presetEditMode ? (
                <Tooltip
                  content={
                    msg.canvasGraph.graphSidePanel.layoutSettings
                      .stpiExplanation
                  }
                >
                  <i className='graph-stpi-warning__icon fa fa-exclamation-circle px-2.5' />
                </Tooltip>
              ) : (
                <Tooltip
                  content={
                    msg.canvasGraph.graphSidePanel.layoutSettings
                      .protocolControls.visibility
                  }
                >
                  <Button
                    variant='tertiary'
                    className='mx-2'
                    onClick={handleEdgeVisibilityIconClick}
                    type='button'
                    size='xxxs'
                    disabled={props.groupGrouped || !props.groupVisible}
                    icon={props.visible ? 'fa-eye' : 'fa-eye-slash'}
                  />
                </Tooltip>
              )}
              <div className='inline-block'>
                <Tooltip
                  content={
                    msg.canvasGraph.graphSidePanel.layoutSettings
                      .protocolControls.grouping
                  }
                >
                  <Button
                    variant='tertiary'
                    className='mx-2'
                    onClick={handleGroupEdgeIconClick}
                    type='button'
                    size='xxxs'
                    disabled={
                      (ctxProps.stpiIsolated && props.protocolName !== 'stp') ||
                      props.groupGrouped ||
                      !props.visible ||
                      !props.groupVisible
                    }
                    icon={props.grouped ? 'fa-link' : 'fa-unlink'}
                  />
                </Tooltip>
              </div>
              {ctxProps.presetEditMode && (
                <>
                  <div className='inline-block '>
                    <Tooltip
                      content={
                        msg.canvasGraph.graphSidePanel.layoutSettings
                          .protocolControls.settings
                      }
                    >
                      <Button
                        variant='tertiary'
                        className='mx-2'
                        onClick={handleOpenProtocolSettingsWindow}
                        type='button'
                        size='xxxs'
                        disabled={
                          ctxProps.stpiIsolated && props.protocolName !== 'stp'
                        }
                        icon='fa-pen'
                      />
                    </Tooltip>
                  </div>
                  <div className='inline-block w-auto'>
                    <Tooltip
                      content={
                        msg.canvasGraph.graphSidePanel.layoutSettings
                          .protocolControls.delete
                      }
                    >
                      <Button
                        variant='tertiary'
                        className={classNames(
                          'mx-2',
                          props.disableDeleteButton && 'invisible',
                        )}
                        type='button'
                        size='xxxs'
                        icon='fa-trash-can'
                        onClick={handleEdgeGroupDelete}
                        disabled={props.disableDeleteButton}
                      />
                    </Tooltip>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
      {props.protocolName === 'stp' &&
        !ctxProps.presetEditMode &&
        props.stpInstancesHook && (
          <GraphStpInstances stpInstancesHook={props.stpInstancesHook} />
        )}
    </>
  );
};

export const TopologyLayoutSettingsProtocolsProtocol = memo(
  BareTopologyLayoutSettingsProtocolsProtocol,
);
